import React, { useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { CheckIcon, SortIcon } from '@components/Icons';
import DropdownMenu from '@components/Menus/DropdownMenu';
import {
  selectIsUpcomingBirthdaysSearch,
  selectSearchSort,
  sortAllPeopleSearch,
  sortSearch,
} from '@actions/search';
import styles from './Sorting.module.scss';
import { ID, SearchSortBy, SearchSortDirection, SearchType } from '@app/types';
import { sortGroup } from '@actions/groups';

function SortIndicatorIcon({ active = false }: { active?: boolean }) {
  return (
    <span className={cn(styles.icon, active && styles.iconActive)}>
      <CheckIcon customColor="#2686be" strokeWidth={2.4} width={11} />
    </span>
  );
}

const sortBySearchType = {
  search: sortSearch,
  allPeopleSearch: sortAllPeopleSearch,
  groupSearch: sortGroup,
};

const sortByLabelMap: Record<Partial<Exclude<SearchSortBy, 'firstInteraction'>>, string> = {
  closeness: 'Relevance',
  firstName: 'First Name',
  lastName: 'Last Name',
  lastInteraction: 'Recency',
  created: 'Created',
};

export default function Sorting({
  searchType,
  resourceID,
}: {
  searchType: SearchType;
  resourceID?: ID;
}) {
  const dispatch = useDispatch();
  const isUpcomingBirthdaySearch = useSelector(selectIsUpcomingBirthdaysSearch);
  const sorting = useSelector(selectSearchSort(searchType, resourceID));
  const [isOpen, setIsOpen] = useState(false);
  const disabled = isUpcomingBirthdaySearch && searchType === 'search';

  let directionLabels: { asc: string; desc: string } = { asc: 'Ascending', desc: 'Descending' };

  if (['firstName', 'lastName'].includes(sorting.name)) {
    directionLabels = { asc: 'Alphabetically', desc: 'Reverse Alphabetically' };
  } else if (['lastInteraction', 'created'].includes(sorting.name)) {
    directionLabels = { asc: 'Oldest First', desc: 'Newest First' };
  } else if (sorting.name === 'closeness') {
    directionLabels = { asc: 'Least Relevant First', desc: 'Most Relevant First' };
  }

  const selectSortValue = (name: SearchSortBy) => {
    if (sorting.name === name) {
      return;
    }

    dispatch(sortBySearchType[searchType](name, sorting.direction, resourceID));
  };

  const selectSortDirection = (direction: SearchSortDirection) => {
    if (sorting.direction === direction) {
      return;
    }

    dispatch(sortBySearchType[searchType](sorting.name, direction, resourceID));
  };

  const descProps = {
    title: directionLabels.desc,
    action: () => selectSortDirection('desc'),
    suffix: <SortIndicatorIcon active={sorting.direction === 'desc'} />,
  };
  const ascProps = {
    title: directionLabels.asc,
    action: () => selectSortDirection('asc'),
    suffix: <SortIndicatorIcon active={sorting.direction === 'asc'} />,
  };

  let orderOptions = [descProps, ascProps];

  if (['firstName', 'lastName'].includes(sorting.name)) {
    orderOptions = [ascProps, descProps];
  }

  return (
    <DropdownMenu
      menuClassName={styles.menu}
      disabled={disabled}
      triggerProps={{
        children: (
          <div className={cn(styles.trigger, isOpen && styles.open)}>
            {!disabled && <span>{sortByLabelMap[sorting.name]}</span>}
            <SortIcon />
          </div>
        ),
      }}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={[
        {
          title: sortByLabelMap.closeness,
          action: () => selectSortValue('closeness'),
          suffix: <SortIndicatorIcon active={!sorting.name || sorting.name === 'closeness'} />,
        },
        {
          title: sortByLabelMap.firstName,
          action: () => selectSortValue('firstName'),
          suffix: <SortIndicatorIcon active={sorting.name === 'firstName'} />,
        },
        {
          title: sortByLabelMap.lastName,
          action: () => selectSortValue('lastName'),
          suffix: <SortIndicatorIcon active={sorting.name === 'lastName'} />,
        },
        {
          title: sortByLabelMap.lastInteraction,
          action: () => selectSortValue('lastInteraction'),
          suffix: <SortIndicatorIcon active={sorting.name === 'lastInteraction'} />,
        },
        {
          title: sortByLabelMap.created,
          action: () => selectSortValue('created'),
          suffix: <SortIndicatorIcon active={sorting.name === 'created'} />,
        },
        {
          divider: true,
          disabled: true,
        },
        ...orderOptions,
      ]}
    />
  );
}
